/**
 * Types and default definition for the offer PDF template designer.
 * Used when rendering from a stored template (full designer) instead of fixed templateId.
 */

export type OfferPdfSectionType =
  | 'accentBar'
  | 'letterhead'
  | 'titleMeta'
  | 'intro'
  | 'lineItemsTable'
  | 'terms'
  | 'notes'
  | 'total';

/** Optional exact position in mm (from top-left of page). When set, the section is placed at this location instead of stacking. */
export interface OfferPdfSectionPosition {
  xMm?: number;
  yMm?: number;
  widthMm?: number;
  heightMm?: number;
}

export interface OfferPdfSectionConfig {
  id: string;
  type: OfferPdfSectionType;
  enabled: boolean;
  order: number;
  /** Exact position (mm). When set, section is drawn at (xMm, yMm) with optional width/height. */
  position?: OfferPdfSectionPosition;
  options?: Record<string, unknown>;
}

export interface OfferPdfPageConfig {
  format: 'a4' | 'letter';
  orientation: 'portrait' | 'landscape';
  marginMm: number;
}

export interface OfferPdfTokens {
  accentColor?: [number, number, number];
  accentBarHeightMm?: number;
  metaBoxColor?: [number, number, number];
  termsBoxColor?: [number, number, number];
  tableHeaderColor?: [number, number, number];
  borderColor?: [number, number, number];
  primaryTextColor?: [number, number, number];
  secondaryTextColor?: [number, number, number];
  titleFontSize?: number;
  bodyFontSize?: number;
  smallFontSize?: number;
}

export interface OfferPdfTableConfig {
  showProductImages?: boolean;
  alternateRowFill?: [number, number, number] | null;
  headerStyle?: 'filled' | 'bordered' | 'plain';
}

export interface OfferPdfTemplateDefinition {
  id?: string;
  name?: string;
  isDefault?: boolean;
  page: OfferPdfPageConfig;
  sections: OfferPdfSectionConfig[];
  tokens: OfferPdfTokens;
  table?: OfferPdfTableConfig;
}

const CLASSIC_SECTION_TYPES: OfferPdfSectionType[] = [
  'accentBar',
  'letterhead',
  'titleMeta',
  'intro',
  'lineItemsTable',
  'total',
  'terms',
  'notes',
];

/** Example position (mm) per section type for new layouts – A4 portrait, margin 16mm. */
const DEFAULT_SECTION_POSITIONS: Record<OfferPdfSectionType, OfferPdfSectionPosition> = {
  accentBar: { yMm: 0, heightMm: 5 },
  letterhead: { xMm: 16, yMm: 15, widthMm: 90, heightMm: 45 },
  titleMeta: { xMm: 110, yMm: 15, widthMm: 84, heightMm: 35 },
  intro: { xMm: 16, yMm: 75, widthMm: 178, heightMm: 15 },
  lineItemsTable: { xMm: 16, yMm: 95, widthMm: 178, heightMm: 100 },
  total: { xMm: 16, yMm: 200, widthMm: 178, heightMm: 20 },
  terms: { xMm: 16, yMm: 225, widthMm: 178, heightMm: 25 },
  notes: { xMm: 16, yMm: 255, widthMm: 178, heightMm: 15 },
};

/** Default definition that reproduces the current Classic layout, with example positions filled in. */
export const DEFAULT_CLASSIC_DEFINITION: OfferPdfTemplateDefinition = {
  name: 'Classic',
  page: {
    format: 'a4',
    orientation: 'portrait',
    marginMm: 16,
  },
  sections: CLASSIC_SECTION_TYPES.map((type, index) => ({
    id: type,
    type,
    enabled: true,
    order: index,
    position: DEFAULT_SECTION_POSITIONS[type],
  })),
  tokens: {
    accentColor: [41, 98, 255],
    accentBarHeightMm: 5,
    metaBoxColor: [245, 247, 250],
    termsBoxColor: [248, 250, 252],
    tableHeaderColor: [52, 73, 94],
    borderColor: [220, 223, 228],
    primaryTextColor: [30, 41, 59],
    secondaryTextColor: [71, 85, 105],
    titleFontSize: 20,
    bodyFontSize: 9,
    smallFontSize: 8,
  },
  table: {
    showProductImages: true,
    alternateRowFill: [250, 250, 252],
    headerStyle: 'filled',
  },
};
